import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    format,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    subWeeks,
    subMonths
} from 'date-fns';
import { XMarkIcon, ArrowDownTrayIcon, CalendarDaysIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import FilterDropdown from '../common/FilterDropdown';
import { Calendar } from '../common/Calendar';
import { exportDisciplineReportExcel, exportDisciplineReportPDF } from '../../utils/disciplineReportExport';

const WEEK = { weekStartsOn: 1 };

const PRESETS = [
    { key: 'this_week', label: 'This Week', range: () => ({ from: startOfWeek(new Date(), WEEK), to: endOfWeek(new Date(), WEEK) }) },
    {
        key: 'last_week',
        label: 'Last Week',
        range: () => {
            const d = subWeeks(new Date(), 1);
            return { from: startOfWeek(d, WEEK), to: endOfWeek(d, WEEK) };
        }
    },
    { key: 'this_month', label: 'This Month', range: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
    {
        key: 'last_month',
        label: 'Last Month',
        range: () => {
            const d = subMonths(new Date(), 1);
            return { from: startOfMonth(d), to: endOfMonth(d) };
        }
    }
];

const labelCls = 'block text-[10px] font-black uppercase tracking-widest text-[#18216D] mb-1.5';
const sectionCls = 'bg-slate-50/70 border border-slate-100 rounded-2xl p-4';
const toggleCls = (active) =>
    `flex-1 px-4 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider border-2 transition-all ${
        active ? 'bg-[#18216D] border-[#18216D] text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-[#18216D]/40'
    }`;

const ClassHouseReportModal = ({ isOpen, onClose }) => {
    const { get } = useApi();
    const { showToast } = useAppState();

    const [groupBy, setGroupBy] = useState('class');
    const [preset, setPreset] = useState('this_week');
    const [range, setRange] = useState(PRESETS[0].range());
    const [showCalendar, setShowCalendar] = useState(false);
    const [targetId, setTargetId] = useState('all');
    const [entryType, setEntryType] = useState('all');
    const [fileFormat, setFileFormat] = useState('pdf');
    const [exporting, setExporting] = useState(false);

    const { data: options } = useQuery({
        queryKey: ['discipline-report-options'],
        queryFn: () => get('/api/discipline/records/report-options/'),
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    useEffect(() => {
        if (!isOpen) return undefined;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // Class and house lists are different; reset the target when the report type changes
    useEffect(() => { setTargetId('all'); }, [groupBy]);

    const targetOptions = useMemo(() => {
        const list = groupBy === 'class' ? options?.classes : options?.houses;
        const allLabel = groupBy === 'class' ? 'All My Classes' : 'All Houses';
        return [
            { name: allLabel, value: 'all' },
            ...(list || []).map(i => ({ name: i.name, value: String(i.id) }))
        ];
    }, [options, groupBy]);

    if (!isOpen) return null;

    const rangeReady = range?.from && range?.to;

    const selectPreset = (p) => {
        setPreset(p.key);
        setRange(p.range());
    };

    const handleRangeSelect = (next) => {
        setPreset('custom');
        setRange(next);
    };

    const handleExport = async () => {
        // A single clicked day is a valid one-day range
        const from = range?.from;
        const to = range?.to || range?.from;
        if (!from) {
            showToast?.('Choose a date range first', 'error');
            return;
        }
        setExporting(true);
        try {
            const params = new URLSearchParams({
                group_by: groupBy,
                date_from: format(from, 'yyyy-MM-dd'),
                date_to: format(to, 'yyyy-MM-dd')
            });
            if (targetId !== 'all') params.set(groupBy === 'class' ? 'stream_id' : 'house_id', targetId);
            if (entryType !== 'all') params.set('type', entryType);

            const report = await get(`/api/discipline/records/report/?${params.toString()}`);
            if (!report.students.length) {
                showToast?.('No scholars found for this selection', 'error');
                return;
            }
            const targetLabel = targetId === 'all'
                ? targetOptions[0].name
                : targetOptions.find(o => o.value === targetId)?.name;

            if (fileFormat === 'excel') exportDisciplineReportExcel(report, targetLabel);
            else await exportDisciplineReportPDF(report, targetLabel);
            showToast?.('Report exported', 'success');
            onClose();
        } catch (err) {
            showToast?.(err?.response?.data?.detail || err.message || 'Failed to export report', 'error');
        } finally {
            setExporting(false);
        }
    };

    const rangeLabel = rangeReady
        ? `${format(range.from, 'd MMM yyyy')} - ${format(range.to, 'd MMM yyyy')}`
        : range?.from
            ? `${format(range.from, 'd MMM yyyy')} - pick end date`
            : 'Select dates';

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6 bg-slate-900/50 backdrop-blur-sm overflow-hidden">
            <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl border border-slate-100 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]">
                <div className="px-7 pt-6 pb-5 shrink-0 relative">
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-2xl bg-[#FFC425]/20 flex items-center justify-center shrink-0">
                            <DocumentChartBarIcon className="w-6 h-6 text-[#18216D]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                                Export Character Report
                            </h3>
                            <p className="text-slate-400 text-xs font-bold mt-1">
                                Merits, demerits and net standing per scholar for any date range.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-[#18216D] hover:bg-slate-100 p-2 rounded-xl transition-colors -mt-1 -mr-2"
                            aria-label="Close"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="absolute left-7 right-7 bottom-0 h-0.5 rounded-full bg-gradient-to-r from-[#FFC425] via-[#FFC425]/40 to-transparent" />
                </div>

                <div className="px-7 pt-6 pb-8 space-y-5 flex-1 overflow-y-auto custom-scrollbar">
                    <div className={sectionCls}>
                        <label className={labelCls}>Report Type</label>
                        <div className="flex gap-3">
                            <button type="button" className={toggleCls(groupBy === 'class')} onClick={() => setGroupBy('class')}>
                                Class Report
                            </button>
                            <button type="button" className={toggleCls(groupBy === 'house')} onClick={() => setGroupBy('house')}>
                                House Report
                            </button>
                        </div>
                        {groupBy === 'house' && options?.scope === 'class_teacher' && (
                            <p className="text-[10px] text-slate-400 font-bold mt-2">
                                Only scholars from your own class appear in each house.
                            </p>
                        )}

                        <label className={`${labelCls} mt-4`}>{groupBy === 'class' ? 'Class' : 'House'}</label>
                        <FilterDropdown
                            className="w-full"
                            value={targetId}
                            onChange={(val) => setTargetId(val || 'all')}
                            options={targetOptions}
                        />
                    </div>

                    <div className={sectionCls}>
                        <label className={labelCls}>Date Range</label>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => selectPreset(p)}
                                    className={`px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border-b-2 border transition-all ${
                                        preset === p.key
                                            ? 'bg-[#18216D] border-[#18216D] border-b-[#FFC425] text-white'
                                            : 'bg-white border-slate-200 text-slate-500 hover:border-[#18216D]/40'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowCalendar(v => !v)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 hover:border-[#FFC425] text-left transition-all"
                        >
                            <span className="text-[11px] font-black uppercase tracking-wider text-[#18216D]">{rangeLabel}</span>
                            <CalendarDaysIcon className="w-5 h-5 text-[#E0A800]" />
                        </button>
                        {showCalendar && (
                            <div className="mt-3 flex justify-center rounded-2xl border border-slate-200 bg-white py-2 shadow-sm">
                                <Calendar
                                    mode="range"
                                    weekStartsOn={1}
                                    selected={range}
                                    defaultMonth={range?.from}
                                    onSelect={handleRangeSelect}
                                />
                            </div>
                        )}
                    </div>

                    <div className={sectionCls}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>Entries</label>
                                <FilterDropdown
                                    className="w-full"
                                    value={entryType}
                                    onChange={(val) => setEntryType(val || 'all')}
                                    options={[
                                        { name: 'Merits & Demerits', value: 'all' },
                                        { name: 'Merits only', value: 'merit' },
                                        { name: 'Demerits only', value: 'demerit' }
                                    ]}
                                    up
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Format</label>
                                <FilterDropdown
                                    className="w-full"
                                    value={fileFormat}
                                    onChange={(val) => setFileFormat(val || 'pdf')}
                                    options={[
                                        { name: 'PDF - scholar summary', value: 'pdf' },
                                        { name: 'Excel - summary + incident log', value: 'excel' }
                                    ]}
                                    up
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-slate-100 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting || !range?.from}
                        className="flex items-center gap-2 px-6 py-2.5 bg-[#FFC425] hover:bg-[#F2B300] text-[#18216D] rounded-xl text-xs font-black uppercase tracking-wider shadow-md shadow-amber-500/20 transition-all active:scale-95 disabled:opacity-50"
                    >
                        <ArrowDownTrayIcon className="w-4 h-4 stroke-[2.5]" />
                        {exporting ? 'Preparing...' : 'Export'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClassHouseReportModal;
